import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { useParams } from 'react-router'
import { Box, Button, InputRadio, Typography } from 'shared/ui'
import { LINK_HELP_HUBSPOT_CALENDAR } from 'shared/constants/links'
import { AlertCalendlyFreePlan } from 'entities/Integrations'
import { AgentFooterButtons } from 'pages/chatbot/ui'
import { useChatbotContext } from 'pages/chatbot/context'
import { useBookAppointmentBlocker } from 'pages/chatbot/pages/agentBookAppointment/hooks/useBookAppointmentBlocker'
import { SmartMeeting } from 'pages/chatbot/pages/agentBookAppointment/pages/connectCalendar/ui/SmartMeeting'
import { LinkTriggerContent } from './LinkTriggerContent'
import { SelectValueDropdown } from './SelectValueDropdown'
import { LinkDropdownCustomComponent } from './LinkDropdownCustomComponent'
import styles from './styles.module.scss'

export const ConnectCalendar = observer(() => {
  const chatbotStore = useChatbotContext()
  const { agentId: id } = useParams()
  const {
    agentBookAppointmentStepsStore: {
      connectCalendarStore,
      isExistChanges,
      confirmLeavePage,
      decreaseStep,
      agentActivateStore: {
        handleActivateClickWithModal,
        isFromContactOwner,
        isSendFromInitialized,
      },
    },
    model: { isActive, sendFrom },
  } = chatbotStore
  const {
    bookingTypes,
    bookingType,
    handleNextClick,
    setBookingType,
    isLoading,
    isConnectionSelected,
    connectionList,
    selectedConnectionItem,
    setConnection,
    errorsMap,
    removeError,
    isMeetingLoading,
    meetingsList,
    search,
    setSearch,
    setLink,
    eventLink,
    hasMoreMeetings,
    isLoadingMoreMeetings,
    loadMoreMeetings,
    handleSaveDraft,
    disabled,
    smartBookingEnabled,
    setSmartBookingEnabled,
    isHubspotSelected,
  } = connectCalendarStore

  const connectionErrorMessage = errorsMap.get('connection')
  const eventLinkErrorMessage = errorsMap.get('book_appointment_link')
  const isContactOwner = isSendFromInitialized
    ? isFromContactOwner
    : sendFrom?.type === 'from_contact_owner'

  useBookAppointmentBlocker({
    isExistChanges,
    confirmLeavePage: (blocker) =>
      confirmLeavePage(
        blocker,
        id,
        isActive
          ? async (id) => {
              const result = await handleActivateClickWithModal(id, {
                isPublished: isActive,
                shouldRedirect: false,
              })
              return result
            }
          : handleSaveDraft
      ),
  })

  return (
    <Box
      display={'flex'}
      flexDirection={'column'}
      height={'100%'}
      justifyContent={'space-between'}
      paddingLeft={'20px'}
      paddingRight={'20px'}
    >
      <div>
        <Box display={'flex'} flexDirection={'column'} marginTop={4} gap={1} marginBottom={-1.5}>
          <AlertCalendlyFreePlan />
        </Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          marginTop={isHubspotSelected ? '27.5px' : 4}
          gap={isHubspotSelected ? 0.5 : 1}
          marginBottom={3}
        >
          <div className={styles.schedulingTitle}>
            <Typography
              variant={'body-sm-regular'}
              ariaLabel={'SchedulingTool_title'}
              color={'var(--content-primary-secondary)'}
            >
              Scheduling tool
            </Typography>
            {isHubspotSelected && (
              <Button
                typeBtn='informative'
                size='small'
                contained='secondary'
                text='HubSpot setup guide'
                onClick={() => {
                  window.open(LINK_HELP_HUBSPOT_CALENDAR, '_blank')
                }}
                style={{ textDecoration: 'none' }}
              />
            )}
          </div>
          <SelectValueDropdown
            placeholder={'Select your booking page'}
            isValueSelected={isConnectionSelected}
            isLoading={isLoading}
            itemsList={connectionList}
            setSelectedValue={setConnection}
            errorMessage={connectionErrorMessage}
            removeError={() => removeError('connection')}
            ariaLabel={'SelectConnection'}
            selectedItem={selectedConnectionItem}
          />
        </Box>
        {isConnectionSelected && (
          <Box display={'flex'} flexDirection={'column'} marginTop={2} gap={1} marginBottom={3}>
            <Typography
              variant={'body-sm-regular'}
              ariaLabel={'ChooseLink_title'}
              color={'var(--content-primary-secondary)'}
            >
              Choose an event link
            </Typography>
            <SelectValueDropdown
              placeholder={'Select event link'}
              isValueSelected={!!eventLink}
              isLoading={isMeetingLoading}
              errorMessage={eventLinkErrorMessage}
              ariaLabel={'SelectEventLink'}
              triggerContent={<LinkTriggerContent link={eventLink} />}
              customComponent={(onClose) => (
                <LinkDropdownCustomComponent
                  onClose={onClose}
                  search={search}
                  setSearch={setSearch}
                  itemsList={meetingsList}
                  setItem={setLink}
                  removeError={() => removeError('book_appointment_link')}
                  hasMore={hasMoreMeetings}
                  isLoadingMore={isLoadingMoreMeetings}
                  onLoadMore={loadMoreMeetings}
                />
              )}
            />
          </Box>
        )}
        <Typography
          variant={'body-lg-medium'}
          ariaLabel={'ConversationTitle'}
          color={'var(--content-primary-primary)'}
          marginBottom={4}
        >
          How should the AI Agent handle booking?
        </Typography>
        <Box display={'flex'} flexDirection={'column'} marginTop={2} marginBottom={3}>
          {bookingTypes.map((item) => (
            <InputRadio
              key={item.id}
              label={item.customLabel}
              checked={bookingType === item.id}
              onChecked={() => setBookingType(item.id)}
              disabled={item.disabled}
              className={classNames(styles.bookingRadio, {
                [styles.checked]: bookingType === item.id,
                [styles.disabled]: item.disabled,
              })}
              radioSize='small'
            />
          ))}
        </Box>
        <SmartMeeting
          active={smartBookingEnabled}
          onChange={setSmartBookingEnabled}
          disabled={isContactOwner}
          switchTooltipProps={{
            label: "Not available when 'Send as contact owner' is selected.",
            visibility: isContactOwner ? 'visible' : 'hidden',
            placement: 'top',
            breakWord: true,
          }}
        />
      </div>

      <AgentFooterButtons
        decreaseStep={decreaseStep}
        handleNextClick={() => handleNextClick(id)}
        isNextButtonDisabled={disabled}
      />
    </Box>
  )
})
